import React, { useState, useEffect } from 'react';

import { Plus, Edit, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { pageAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const PageManagement = () => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    metaTitle: '',
    metaDescription: '',
    isPublished: true,
    showInFooter: true,
    footerSection: 'support',
    order: 0
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await pageAPI.getAllAdmin();
      setPages(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch pages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPage) {
        await pageAPI.update(editingPage._id, formData);
        toast.success('Page updated');
      } else {
        await pageAPI.create(formData);
        toast.success('Page created');
      }
      
      fetchPages();
      resetForm();
    } catch (error) {
      toast.error(error.message || 'Failed to save page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      content: page.content,
      excerpt: page.excerpt || '',
      metaTitle: page.metaTitle || '',
      metaDescription: page.metaDescription || '',
      isPublished: page.isPublished,
      showInFooter: page.showInFooter,
      footerSection: page.footerSection,
      order: page.order
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    
    try {
      await pageAPI.delete(id);
      toast.success('Page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPage(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      metaTitle: '',
      metaDescription: '',
      isPublished: true,
      showInFooter: true,
      footerSection: 'support',
      order: 0
    });
  };

  const getSectionLabel = (section) => {
    const labels = {
      support: '📞 Support',
      legal: '⚖️ Legal',
      company: '🏢 Company'
    };
    return labels[section] || section;
  };

  if (isLoading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1><FileText size={24} /> Page Management</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} /> New Page
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPage ? 'Edit Page' : 'Create New Page'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Page Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Footer Section</label>
                  <select
                    value={formData.footerSection}
                    onChange={(e) => setFormData({ ...formData, footerSection: e.target.value })}
                  >
                    <option value="support">Support</option>
                    <option value="legal">Legal</option>
                    <option value="company">Company</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Page content (HTML supported)"
                  rows={12}
                  required
                />
              </div>

              <div className="form-group">
                <label>Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Short description"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    value={formData.metaTitle}
                    onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                    placeholder="SEO Title"
                  />
                </div>
                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Meta Description</label>
                <textarea
                  value={formData.metaDescription}
                  onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                  placeholder="SEO Description"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                  Published
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.showInFooter}
                    onChange={(e) => setFormData({ ...formData, showInFooter: e.target.checked })}
                  />
                  Show in Footer
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingPage ? 'Update' : 'Create'} Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pages List */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Section</th>
              <th>Status</th>
              <th>Footer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">No pages found</td>
              </tr>
            ) : (
              pages.map((page) => (
                <tr key={page._id}>
                  <td>
                    <strong>{page.title}</strong>
                  </td>
                  <td>
                    <code>/page/{page.slug}</code>
                  </td>
                  <td>{getSectionLabel(page.footerSection)}</td>
                  <td>
                    {page.isPublished ? (
                      <span className="badge success"><Eye size={12} /> Published</span>
                    ) : (
                      <span className="badge warning"><EyeOff size={12} /> Draft</span>
                    )}
                  </td>
                  <td>
                    {page.showInFooter ? (
                      <span className="badge info">Yes</span>
                    ) : (
                      <span className="badge">No</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <a 
                        href={`/page/${page.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-icon"
                        title="View"
                      >
                        <Eye size={16} />
                      </a>
                      <button 
                        className="btn-icon"
                        onClick={() => handleEdit(page)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => handleDelete(page._id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PageManagement;
